<template>
  <div class="auth-wrapper">
    <div class="auth-container">
      <div class="auth-content-container">
        <v-card class="auth-card">
          <!-- TODO: Change domain name / add i18n to line below -->
          <div class="auth-title">Welcome to {{ appName }}!</div>
          <div class="auth-subtitle">{{ $t("auth.login_to_your_account") }}</div>

          <VaDisclaimer
            v-if="errorMessages"
            class="error-disclaimer va-disclaimer--caption"
            :type="'error'"
            :icon-color="'error'"
            :text="errorMessages">
          </VaDisclaimer>

          <div v-if="$can('view', 'microsoft login button')" class="zitadel-login__wrapper text-center">
            <ZitadelLoginModule @error="handleZitadelError" />
          </div>

          <div v-if="$can('view', 'google login button')" class="google-login__wrapper text-center">
            <GoogleLoginModule />
          </div>

          <div v-if="$can('view', 'google login button')" class="auth-divider-wrapper">
            <div class="auth-divider"></div>
            <div>or</div>
            <div class="auth-divider"></div>
          </div>
          <v-form :class="{ 'mt-5': !$can('view', 'google login button') }" ref="form" @submit.prevent="validate">
            <v-text-field
              v-on:keyup.enter="validate"
              :placeholder="$t('auth.email')"
              :rules="[rules.required, rules.email]"
              :label="$t('auth.email')"
              v-model="form.email"
              autocomplete="current-email"
              persistent-placeholder
              hide-details="auto"
              required
              validate-on-blur>
              <template v-slot:message="{ message }">
                <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
                {{ message }}
              </template>
            </v-text-field>
            <div class="spacing-top--24">
              <v-text-field
                class="password-input"
                :label="$t('auth.password')"
                :placeholder="$t('auth.password')"
                v-model="form.password"
                autocomplete="current-password"
                :rules="[rules.required]"
                :append-icon="hidePassword ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
                :type="hidePassword ? 'password' : 'text'"
                @click:append="() => (hidePassword = !hidePassword)"
                required
                validate-on-blur
                persistent-placeholder
                hide-details="auto">
                <template v-slot:message="{ message }">
                  <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
                  {{ message }}
                </template>
              </v-text-field>
            </div>
            <router-link to="/forgot-password">
              <div class="auth-forgot">
                {{ $t("auth.forgot_password") }}
              </div>
            </router-link>
            <v-btn color="primary" :loading="loading" type="submit" x-large class="auth-btn spacing-top--24">
              {{ $t("auth.login") }}
            </v-btn>
            <div class="register" v-if="$can('view', 'register button login page')">
              {{ $t("auth.register") }}
              <router-link to="/register">{{ $t("auth.register_link") }}</router-link>
            </div>
          </v-form>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import { mdiEyeOutline, mdiEyeOffOutline, mdiAlertCircle } from "@mdi/js";
  import themeConfig from "@themeConfig";
  import useAppConfig from "@core/@app-config/useAppConfig";
  import { validateEmail } from "@/assets/utilities/helpers/email";
  import GoogleLoginModule from "@/components/custom/Auth/GoogleLoginModule.vue";
  import ZitadelLoginModule from "@/components/custom/Auth/ZitadelLoginModule.vue";
  import VaDisclaimer from "@/components/custom/VaDisclaimer.vue";

  export default {
    components: {
      VaDisclaimer,
      GoogleLoginModule,
      ZitadelLoginModule,
    },
    data() {
      const { appSkinVariant } = useAppConfig();

      return {
        validateEmail,
        appSkinVariant,
        icons: {
          mdiEyeOutline: mdiEyeOutline,
          mdiEyeOffOutline: mdiEyeOffOutline,
          mdiAlertCircle: mdiAlertCircle,
        },
        rules: {
          required: value => !!value || this.$t("generic.validation.required"),
          email: value => !!validateEmail(value) || this.$t("generic.validation.email"),
        },
        loading: false,
        appName: themeConfig.app.name,
        appLogo: themeConfig.app.logo,
        form: {
          email: "",
          password: "",
        },
        hidePassword: true,
        errorMessages: "",
        isPasswordVisible: false,
      };
    },
    methods: {
      ...mapActions({
        login: "auth/login",
      }),
      async validate() {
        this.errorMessages = "";
        if (this.$refs.form.validate()) {
          this.loading = true;
          try {
            const response = await this.login(this.form);
            this.$store.dispatch("SET_USER_DATA", response.user);
            this.$router.push({ name: "dashboard" });
          } catch (e) {
            this.errorMessages = e.message;
          } finally {
            this.loading = false;
          }
        }
      },
      handleZitadelError(error) {
        this.errorMessages = error;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .error-disclaimer {
    margin-top: 24px;
    @media (min-width: 800px) {
      margin-top: 32px;
    }
  }

  .google-login {
    &__wrapper {
      margin-top: 24px;
      @media (min-width: 800px) {
        margin-top: 32px;
      }
    }
  }

  .zitadel-login {
    &__wrapper {
      margin-top: 24px;
      @media (min-width: 800px) {
        margin-top: 32px;
      }
    }
  }

  .auth-forgot {
    @include text-caption();
    color: get-color("primary");
    float: right;
    margin-top: 8px;

    &:hover {
      color: get-color("primary-hover");
    }
  }

  .password-input {
    ::v-deep {
      .v-input__slot {
        .v-input__icon {
          .v-icon--link {
            color: get-color("icon");
          }
        }
      }
    }
  }

  .auth-wrapper {
    width: 100%;
    @media (min-width: 600px) {
      margin-top: 32px;
    }
    a {
      text-decoration: unset;
    }
  }

  .auth-container {
    display: flex;
    justify-content: center;
    padding: 4px;
    @media (min-width: 600px) {
      padding: 0;
    }
    .auth-content-container {
      width: 100%;
      @media (min-width: 600px) {
        width: 398px;
      }
    }
  }

  .auth-card {
    padding: 24px;
    border-radius: 6px;
    @media (min-width: 600px) {
      padding: 32px;
    }

    .auth-title {
      @include heading-4();
      color: get-color("headline");
      margin-bottom: 4px;
    }
    .auth-subtitle {
      @include text-body-2();
      color: get-color("subtitle");
    }

    .auth-divider-wrapper {
      @include text-caption();
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin: 24px 0;
      .auth-divider {
        height: 1px;
        border-radius: 4px;
        width: 35%;
        background: get-color("border");
      }
      @media (min-width: 600px) {
        margin: 32px 0;
      }
    }

    .auth-btn {
      @include text-button-medium();
      background-color: get-color("primary");
      width: 100%;
      color: white;
      padding: 12px 54px;
      border-radius: 4px;

      &:hover {
        background-color: get-color("primary-hover");
      }
    }

    .register {
      @include text-caption();
      color: get-color("headline");
      text-align: center;
      margin-top: 16px;
    }
  }
</style>
